import { useCallback, useEffect, useMemo, useState } from "react";
import { createDefaultProjectionModel } from "@/domain/projection/defaults";
import {
  analyzeBreakEven,
  cloneProjectionModel,
  compareAlternatives,
  createDefaultWorkspace,
  modelsEqual,
  nextAlternativeName,
  parseProjectionWorkspace,
  projectMonths,
  simulate,
  simulateScenarios,
  summarizeCosts,
} from "@/domain/projection/engine";
import type { ProjectionModel, ProjectionWorkspace } from "@/domain/projection/types";

const MODEL_STORAGE_KEY = "coofit.projection.model.v1";
const WORKSPACE_STORAGE_KEY = "coofit.projection.workspace.v1";

function loadWorkspace(): ProjectionWorkspace {
  if (typeof window === "undefined") {
    return createDefaultWorkspace();
  }
  try {
    const workspaceRaw = window.localStorage.getItem(WORKSPACE_STORAGE_KEY);
    if (workspaceRaw) {
      const parsed = parseProjectionWorkspace(JSON.parse(workspaceRaw));
      if (parsed) return parsed;
    }
    const modelRaw = window.localStorage.getItem(MODEL_STORAGE_KEY);
    if (modelRaw) {
      const parsed = parseProjectionWorkspace(JSON.parse(modelRaw));
      if (parsed) return parsed;
    }
  } catch {
    /* keep default */
  }
  return createDefaultWorkspace();
}

function persistWorkspace(workspace: ProjectionWorkspace) {
  window.localStorage.setItem(WORKSPACE_STORAGE_KEY, JSON.stringify(workspace));
  window.localStorage.setItem(MODEL_STORAGE_KEY, JSON.stringify(workspace.draft));
}

export function useCostProjection() {
  const [workspace, setWorkspace] = useState<ProjectionWorkspace>(loadWorkspace);

  useEffect(() => {
    persistWorkspace(workspace);
  }, [workspace]);

  const model = workspace.draft;
  const currentAlternative = workspace.alternatives.find((item) => item.id === workspace.currentId) ?? null;
  const dirty = currentAlternative ? !modelsEqual(model, currentAlternative.model) : true;

  const result = useMemo(() => simulate(model), [model]);
  const costs = useMemo(
    () => summarizeCosts(model, model.adoption.includeDebt),
    [model],
  );
  const scenarioResults = useMemo(() => simulateScenarios(model), [model]);
  const monthRows = useMemo(() => projectMonths(model), [model]);
  const breakEven = useMemo(() => analyzeBreakEven(result), [result]);

  const setModel = useCallback((next: ProjectionModel | ((prev: ProjectionModel) => ProjectionModel)) => {
    setWorkspace((current) => ({
      ...current,
      draft: typeof next === "function" ? next(current.draft) : next,
    }));
  }, []);

  const resetToDefaults = useCallback(() => {
    setWorkspace((current) => ({
      ...current,
      currentId: null,
      draft: createDefaultProjectionModel(),
    }));
  }, []);

  const saveCurrent = useCallback((name?: string, notes?: string) => {
    setWorkspace((current) => {
      const now = new Date().toISOString();
      if (current.currentId) {
        return {
          ...current,
          alternatives: current.alternatives.map((item) =>
            item.id === current.currentId
              ? {
                  ...item,
                  name: name?.trim() || item.name,
                  notes: notes ?? item.notes,
                  savedAt: now,
                  model: cloneProjectionModel(current.draft),
                }
              : item,
          ),
        };
      }
      const id = `alt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
      return {
        ...current,
        currentId: id,
        alternatives: [
          ...current.alternatives,
          {
            id,
            name: name?.trim() || nextAlternativeName(current.alternatives),
            notes: notes ?? "",
            savedAt: now,
            model: cloneProjectionModel(current.draft),
          },
        ],
      };
    });
  }, []);

  const saveAsNew = useCallback((name: string, notes = "") => {
    const id = `alt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
    setWorkspace((current) => ({
      ...current,
      currentId: id,
      alternatives: [
        ...current.alternatives,
        {
          id,
          name: name.trim() || nextAlternativeName(current.alternatives),
          notes,
          savedAt: new Date().toISOString(),
          model: cloneProjectionModel(current.draft),
        },
      ],
    }));
  }, []);

  const loadAlternative = useCallback((id: string) => {
    setWorkspace((current) => {
      const alternative = current.alternatives.find((item) => item.id === id);
      if (!alternative) return current;
      return {
        ...current,
        currentId: alternative.id,
        draft: cloneProjectionModel(alternative.model),
      };
    });
  }, []);

  const duplicateAlternative = useCallback((id: string) => {
    setWorkspace((current) => {
      const alternative = current.alternatives.find((item) => item.id === id);
      if (!alternative) return current;
      const newId = `alt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
      const copy: typeof alternative = {
        ...alternative,
        id: newId,
        name: `${alternative.name} (copia)`,
        savedAt: new Date().toISOString(),
        model: cloneProjectionModel(alternative.model),
      };
      return {
        ...current,
        currentId: newId,
        draft: cloneProjectionModel(alternative.model),
        alternatives: [...current.alternatives, copy],
      };
    });
  }, []);

  const renameAlternative = useCallback((id: string, name: string, notes?: string) => {
    setWorkspace((current) => ({
      ...current,
      alternatives: current.alternatives.map((item) =>
        item.id === id
          ? { ...item, name: name.trim() || item.name, notes: notes ?? item.notes }
          : item,
      ),
    }));
  }, []);

  const deleteAlternative = useCallback((id: string) => {
    setWorkspace((current) => ({
      ...current,
      currentId: current.currentId === id ? null : current.currentId,
      alternatives: current.alternatives.filter((item) => item.id !== id),
    }));
  }, []);

  const exportJson = useCallback(() => {
    const blob = new Blob([JSON.stringify(workspace, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `coofit-proyeccion-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [workspace]);

  const importJson = useCallback((file: File) => {
    return file.text().then((text) => {
      const raw: unknown = JSON.parse(text);
      const parsed = parseProjectionWorkspace(raw);
      if (!parsed) {
        throw new Error("invalid");
      }
      const isWorkspace = typeof raw === "object" && raw !== null && "draft" in raw;
      setWorkspace((current) => (isWorkspace
        ? parsed
        : { ...current, currentId: null, draft: parsed.draft }));
    });
  }, []);

  const comparisonRows = useMemo(
    () => compareAlternatives(workspace, true),
    [workspace],
  );

  return {
    model,
    setModel,
    workspace,
    currentAlternative,
    dirty,
    result,
    costs,
    scenarioResults,
    monthRows,
    breakEven,
    comparisonRows,
    resetToDefaults,
    saveCurrent,
    saveAsNew,
    loadAlternative,
    duplicateAlternative,
    renameAlternative,
    deleteAlternative,
    exportJson,
    importJson,
  };
}
